import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import gamerjilogo from "../assets/images/gamerjilogo.png";
import languageicon from "../assets/images/languageIcon.svg";
import { fetchHtml5Categories } from "../api/html5GamesApi";

// Define language type
type Language = {
  code: string;
  label: string;
};

// Define category type if needed from API
type Category = {
  name: string;
};

const Gamerji: React.FC = () => {
  const { t } = useTranslation();

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [casualSlugs, setCasualSlugs] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("English");
  const [showLangDropdown, setShowLangDropdown] = useState<boolean>(false);
  const location = useLocation();

  const languages: Language[] = [
    { code: "en", label: "English" },
    { code: "hi", label: "हिंदी" },
    { code: "ar", label: "عربي" },
  ];

  const handleMenuClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLanguageSelect = (lang: Language) => {
    setSelectedLanguage(lang.label);
    setShowLangDropdown(false);
    localStorage.setItem("language", lang.code);
    i18n.changeLanguage(lang.code);
  };

  useEffect(() => {
    const savedLang = localStorage.getItem("language");
    if (savedLang) {
      const langObj = languages.find((l) => l.code === savedLang);
      if (langObj) setSelectedLanguage(langObj.label);
      i18n.changeLanguage(savedLang);
    }
  }, []);

  useEffect(() => {
    const getCategories = async () => {
      try {
        const res = await fetchHtml5Categories({ skip: 0, limit: 100 });
        const all: Category[] = res?.list || [];
        const slugs = all.map(
          (cat) =>
            `/category/${cat.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`
        );
        setCasualSlugs(["/casual-games", ...slugs]);
      } catch (err) {
        console.error("Failed to load categories", err);
      }
    };
    getCategories();
  }, []);

  const getNavLinkClass = ({ isActive }: { isActive: boolean }): string =>
    `flex relative py-2 hover:text-[var(--alto)] ${isActive
      ? "lg:after:block lg:after:w-full lg:after:bg-white lg:after:-bottom-1 lg:after:h-1 lg:after:absolute"
      : ""
    }`;

  const isCasualActive = casualSlugs.some(
    (slug) => slug.toLowerCase() === location.pathname.toLowerCase()
  );
  const handleExternalRedirect = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.open("https://portal.gamerji.com/login", "_blank", "noopener,noreferrer");
  };
  return (
    <div className="  fixed z-50 
    top-[1rem] 
    left-0 
    right-0

    sm:top-[1rem]
    md:top-[2rem]   
    lg:top-[3rem]">
      
      <div className="container w-full mx-auto max-h-screen">
        <div className="px-4 lg:px-0 w-full">
          <div
            className={`${isMenuOpen ? "lg:flex" : "flex"
              } items-center relative mt-6 justify-between backdrop-blur-xl bg-[#070B28]/40 rounded-3xl py-4 px-4 lg:mt-0 lg:py-6 lg:px-8 items-start lg:items-center border border-white/10 shadow-header`}
          >
            {/* Logo */}
            <a href="/" target="_self" className="cursor-pointer">
              <img
                src={gamerjilogo}
                alt="Gamerji Logo"
                className={`logo min-w-12 w-12 lg:w-20 xl:w-28 h-auto ${i18n.language === "ar" ? "ms-12" : "me-12"
                  } lg:me-8 xl:me-16`}
              />
            </a>

            {/* Right Section */}
            <div className="flex items-center flex-grow justify-end">
              {/* Mobile Menu Icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                onClick={handleMenuClick}
                className={`w-[24px] h-[24px] mt-1 cursor-pointer md:w-8 lg:hidden bg-white rounded-lg ${isMenuOpen
                    ? "absolute top-4 ltr:right-4 rtl:left-4"
                    : "relative"
                  }`}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>

              {/* Menu Links */}
              <div
                className={`mt-8 gap-6 text-white w-full font-bold flex flex-col text-nowrap text-xs sm:text-sm 2xl:text-base ${isMenuOpen ? "block" : "hidden"
                  } lg:flex lg:flex-row lg:mt-0 lg:justify-between lg:text-xs xl:text-sm 2xl:text-base lg:gap-4 font-proxima`}
              >
                <NavLink to="/" className={getNavLinkClass}>
                  {t("home")}
                </NavLink>

                <NavLink
                  to="/esports"
                  className={`flex relative py-2 hover:text-[var(--alto)] ${location.pathname.startsWith("/esports") ||
                      location.pathname.startsWith("/blog")
                      ? "lg:after:block lg:after:w-full lg:after:bg-white lg:after:-bottom-1 lg:after:h-1 lg:after:absolute"
                      : ""
                    }`}
                >
                  {t("esportsh")}
                </NavLink>

                <NavLink to="/sports-draft" className={getNavLinkClass}>
                  {t("sportsDrafth")}
                </NavLink>

                <NavLink to="/service" className={getNavLinkClass}>
                  {t("services.title")}
                </NavLink>

                <NavLink
                  to="/casual-games"
                  className={`flex relative py-2 hover:text-[var(--alto)] ${isCasualActive
                      ? "lg:after:block lg:after:w-full lg:after:bg-white lg:after:-bottom-1 lg:after:h-1 lg:after:absolute"
                      : ""
                    }`}
                >
                  {t("casualGames")}
                </NavLink>

                <div className="flex items-center justify-end border-t border-[var(--silver)] pt-5 pb-2 lg:p-0 lg:border-none">
                  <div className="lg:border-s lg:border-white lg:ps-4 lg:h-full lg:w-1" />
                  <NavLink
                    to="https://portal.gamerji.com/login"
                    className="flex relative py-2 hover:text-[var(--alto)]"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("loginRegister")}
                  </NavLink>
                </div>

                <NavLink to="https://play.google.com/store/apps/details?id=com.gamerji.sportsdraft"  className="flex relative py-2 hover:text-[var(--alto)]"
                    target="_blank"
                    rel="noopener noreferrer">
                  {t("downloadApp")}
                </NavLink>

                {/* Language Switcher */}
                <div className="flex items-center justify-end border-t border-[var(--silver)] pt-5 pb-2 lg:p-0 lg:border-none">
                  <div className="lg:border-s lg:border-white lg:ps-4 lg:h-full lg:w-1" />
                  <div className="relative">
                    <button
                      onClick={() =>
                        setShowLangDropdown((prev: boolean) => !prev)
                      }
                      className="flex text-center items-center justify-end font-bold"
                    >
                      {selectedLanguage}
                      <img
                        alt="language icon"
                        width={20}
                        height={20}
                        className="w-6 h-6 xl:w-8 xl:h-8 ps-1"
                        src={languageicon}
                      />
                    </button>

                    {showLangDropdown && (
                      <div className="absolute right-0 mt-8 w-32 backdrop-blur-2xl bg-[#070B28]/40 rounded-md shadow-lg z-50">
                        {languages.map((lang) => (
                          <div
                            key={lang.code}
                            className={`px-4 py-2 cursor-pointer text-center 
                              ${selectedLanguage === lang.label
                                ? "text-[rgb(243,36,36)] font-semibold"
                                : "text-white"
                              } 
                              hover:text-[rgb(243,36,36)]`}
                            onClick={() => handleLanguageSelect(lang)}
                          >
                            {lang.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* End Right Section */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Gamerji;
